import { useState, useCallback, useMemo } from 'react';
import type { GlowpickProduct } from '../../../models/Glowpick';
import type { CoupangFilters, CPSearchList, GpKeywordCpSearch } from '../../../models/Coupang';
import { cpSearchClient } from '../../../infrastructure/CpSearchClient';
import { BatchClient, type ScrapeCoupangArgs, type TriggerBatchResponse } from '../../../infrastructure/BatchClient';
import { getConfig } from '../../../config';

export const useCoupang = () => {
  // Data state
  const [coupangProducts, setCoupangProducts] = useState<CPSearchList>([]);
  const [selectedCoupangProduct, setSelectedCoupangProduct] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Filters state
  const [coupangFilters, setCoupangFilters] = useState<CoupangFilters>({
    search: '',
    brand: '',
    category: '',
    subcategory: '',
  });

  // Initialize batch client
  const batchClient = useMemo(() => new BatchClient(getConfig().apiUrl), []);

  // Search Coupang products based on Glowpick product
  const searchCoupangProducts = useCallback(async (glowpickProduct: GlowpickProduct) => {
    setLoading(true);
    try {
      // Fetch GP Keyword CP Search results
      const response = await cpSearchClient.getGpKeywordCpSearch(glowpickProduct.productId);

      // Debug: Log the actual API response
      console.info('🔍 Debug - API response:', response);
      console.info('🔍 Debug - API response.products:', response.products);
      if (response.products && response.products.length > 0) {
        console.info('🔍 Debug - First product structure:', response.products[0]);
      }

      // Transform GpKeywordCpSearch to CoupangProduct for the table component
      const searchResults: GpKeywordCpSearch[] = response.products
        .filter((item: GpKeywordCpSearch) => item.cpProductId && item.cpItemId)
        .map((item: GpKeywordCpSearch) => ({
          ...item,
        }));

      // Sort products: Search rank first, then Rocket delivery, then by number of reviews (descending)
      const sortedProducts = searchResults.sort((a, b) => {
        // First priority: Search rank (ascending - lower rank number = better position)
        const aRanking = a.cpSearchedProduct.ranking;
        const bRanking = b.cpSearchedProduct.ranking;

        // Products with search rank come first
        if (aRanking !== undefined && bRanking === undefined) {
          return -1;
        }
        if (aRanking === undefined && bRanking !== undefined) {
          return 1;
        }
        if (aRanking !== undefined && bRanking !== undefined) {
          return aRanking - bRanking;
        }

        // Second priority: Rocket delivery (Rocket > Seller Rocket > others)
        const getShippingPriority = (shipping: string) => {
          if (shipping === 'Rocket') {
            return 0;
          }
          if (shipping === 'Seller Rocket') {
            return 1;
          }
          return 2;
        };

        const shippingDiff =
          getShippingPriority(a.cpSearchedProduct.shipping || '') -
          getShippingPriority(b.cpSearchedProduct.shipping || '');
        if (shippingDiff !== 0) {
          return shippingDiff;
        }

        // Third priority: Number of reviews (descending)
        return (b.cpSearchedProduct.reviews || 0) - (a.cpSearchedProduct.reviews || 0);
      });

      setCoupangProducts(sortedProducts);
      console.info(`🛒 Loaded ${sortedProducts.length} Coupang products`);

      return sortedProducts;
    } catch (error) {
      console.error('Error fetching GP Keyword CP Search results:', error);
      // Fallback to empty array on error
      setCoupangProducts([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete keyword search results
  const deleteKeywordSearchResults = useCallback(
    async (productId: string): Promise<{ success: boolean; message?: string }> => {
      try {
        console.info(`🗑️ Deleting keyword search results for product ${productId}`);
        const result = await cpSearchClient.deleteGpKeywordCpSearch(productId);

        if (result.success) {
          console.info(`✅ Successfully deleted keyword search results for product ${productId}`);
        } else {
          console.error(`❌ Failed to delete keyword search results for product ${productId}:`, result.message);
        }

        return result;
      } catch (error) {
        console.error(`❌ Error deleting keyword search results for product ${productId}:`, error);
        return { success: false, message: error instanceof Error ? error.message : 'Unknown error' };
      }
    },
    [],
  );

  // Trigger scrape batch job for keyword search
  const triggerScrapeBatchJob = useCallback(
    async (productId: string): Promise<{ success: boolean; jobId?: string; message?: string }> => {
      try {
        console.info(`🔄 Triggering scrape batch job for product ${productId}`);

        const args: ScrapeCoupangArgs = {
          task: 'keywordSearch',
          gpProductIds: [productId],
          forceRewrite: true,
        };

        const response: TriggerBatchResponse = await batchClient.triggerScrapeCoupangJob(args);
        console.info(`✅ Successfully triggered batch job ${response.jobId} for product ${productId}`);

        return { success: true, jobId: response.jobId };
      } catch (error) {
        console.error(`❌ Error triggering batch job for product ${productId}:`, error);
        return { success: false, message: error instanceof Error ? error.message : 'Unknown error' };
      }
    },
    [batchClient],
  );

  // Handle Coupang product selection
  const handleCoupangProductSelect = useCallback((productId: string) => {
    setSelectedCoupangProduct(productId);
  }, []);

  // Update filters
  const updateFilters = useCallback((newFilters: Partial<CoupangFilters>) => {
    setCoupangFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  // Clear products
  const clearCoupangProducts = useCallback(() => {
    setCoupangProducts([]);
    setSelectedCoupangProduct(null);
  }, []);

  return {
    // Data
    coupangProducts,
    selectedCoupangProduct,
    loading,

    // Filters
    coupangFilters,

    // Actions
    searchCoupangProducts,
    triggerScrapeBatchJob,
    deleteKeywordSearchResults,
    handleCoupangProductSelect,
    updateFilters,
    clearCoupangProducts,

    // Setters for direct state updates if needed
    setCoupangProducts,
    setSelectedCoupangProduct,
    setCoupangFilters,
  };
};
